// SH share image: draws the 270x480 preview (QR5_Share) on a canvas at 4x (1080x1920 PNG) for share / save.
// Canvas instead of DOM capture: no dependencies, and Safari does not taint the canvas. The ticket is a simplified
// mini XCard in the buddy's card colours (components/XCard.tsx styles).
import type { CharId } from '@shared/types';
import { C } from '@shared/content/characters';
import { rgba } from '../../theme';

export interface ShareSpec {
  ch: CharId;
  kind: 'card' | 'streak' | 'week';
  quote: string;            // with the curly quotes
  signature: string;        // '— Margo, on Xiaoman'
  streak: number;
  ticket?: { label: string; title: string };
  weekTitle?: string;
  weekStats?: { n: string; t: string }[];
}

const W = 270, H = 480, K = 4;
const FLAME = 'M12 3c1 3.5 5 5.5 5 10a5 5 0 0 1-10 0c0-2.2 1.2-3.6 2.4-4.6.2 1.6 1 2.6 2.1 2.9C11 9 11.2 6 12 3z';
const TICKET: Record<CharId, { bg: string; rad: number; bar?: [string, string]; label: string; ink: string; font: string; upper?: boolean; stamp: string; border?: string }> = {
  sunny: { bg: '#FFFEFA', rad: 5, label: '#7A5A10', ink: '#23211C', font: "700 13px 'Kalam'", stamp: '#B8322E' },
  margo: { bg: '#141312', rad: 10, bar: ['#E0A43A', '#1A1206'], label: '#E0A43A', ink: '#F2EEE6', font: "700 10px 'Space Mono'", upper: true, stamp: '#E0A43A', border: '#3A352D' },
  arjun: { bg: '#F4EFE4', rad: 14, label: '#2E3F7A', ink: '#2E3F7A', font: "700 13px 'Kalam'", stamp: '#2E3F7A' },
  tavo: { bg: '#FBF8F0', rad: 10, bar: ['#2F5D45', '#FFFFFF'], label: '#2F5D45', ink: '#22324A', font: "700 13px 'Kalam'", stamp: '#2F5D45' },
};

/** Card quote size by length (the preview and the PNG use the same): long done lines step down so they stay readable
 *  and clear of the ticket. */
export const quoteSize = (quote: string) => (quote.length <= 62 ? 27 : quote.length <= 96 ? 23 : quote.length <= 140 ? 20 : 18);

const loadImg = (src: string) => new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => rej(new Error('image')); i.src = src; });

function wrap(ctx: CanvasRenderingContext2D, text: string, max: number): string[] {
  const out: string[] = [];
  let line = '';
  for (const w of text.split(/\s+/)) {
    const t = line ? line + ' ' + w : w;
    if (ctx.measureText(t).width > max && line) { out.push(line); line = w; } else line = t;
  }
  if (line) out.push(line);
  return out;
}

function rr(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}

function flame(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, fill: string, stroke?: string) {
  ctx.save(); ctx.translate(x, y); ctx.scale(w / 24, h / 24);
  const p = new Path2D(FLAME);
  ctx.fillStyle = fill; ctx.fill(p);
  if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = 0.6; ctx.stroke(p); }
  ctx.restore();
}

/** Quote + signature anchored so the signature ends `bottom` px above the image's bottom edge, or from `top`
 *  (moved up when needed so it ends above `above`, the ticket's top). */
function quoteBlock(ctx: CanvasRenderingContext2D, s: ShareSpec, size: number, at: { top?: number; bottom?: number; above?: number }) {
  const acc = C[s.ch].acc;
  ctx.font = `italic 400 ${size}px 'Instrument Serif', Georgia, serif`;
  const lines = wrap(ctx, s.quote, W - 36);
  const lh = size * 1.1;
  const sigGap = at.top != null ? 8 : 6;
  const total = lines.length * lh + sigGap + 15;
  let y = at.top != null ? at.top : H - (at.bottom || 0) - total;
  if (at.top != null && at.above != null) y = Math.max(44, Math.min(y, at.above - total));
  ctx.fillStyle = '#F7EEE3'; ctx.shadowColor = 'rgba(0,0,0,0.75)'; ctx.shadowBlur = 12; ctx.shadowOffsetY = 2;
  ctx.textBaseline = 'top';
  for (const l of lines) { ctx.fillText(l, 18, y + (lh - size) / 2); y += lh; }
  ctx.shadowColor = 'transparent';
  ctx.font = "700 12.5px 'Figtree', sans-serif"; ctx.fillStyle = acc;
  ctx.fillText(s.signature, 18, y + sigGap);
}

function ticket(ctx: CanvasRenderingContext2D, ch: CharId, label: string, title: string) {
  const t = TICKET[ch];
  const w = 178, h = 96, x = 16, y = H - 18 - h;
  ctx.save();
  ctx.translate(x + w / 2, y + h / 2); ctx.rotate((-3 * Math.PI) / 180); ctx.translate(-w / 2, -h / 2);
  ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 14; ctx.shadowOffsetY = 6;
  rr(ctx, 0, 0, w, h, t.rad); ctx.fillStyle = t.bg; ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.save(); rr(ctx, 0, 0, w, h, t.rad); ctx.clip();
  if (ch === 'sunny') {
    ctx.strokeStyle = '#C9DAEE'; ctx.lineWidth = 1; for (let ly = 15; ly < h; ly += 15) { ctx.beginPath(); ctx.moveTo(0, ly); ctx.lineTo(w, ly); ctx.stroke(); }
    ctx.strokeStyle = '#E9A0A0'; ctx.beginPath(); ctx.moveTo(13, 0); ctx.lineTo(13, h); ctx.stroke();
  } else if (ch === 'arjun') {
    ctx.strokeStyle = '#DED6C6'; ctx.lineWidth = 1;
    for (let g = 12; g < Math.max(w, h); g += 12) { ctx.beginPath(); ctx.moveTo(g, 0); ctx.lineTo(g, h); ctx.moveTo(0, g); ctx.lineTo(w, g); ctx.stroke(); }
  }
  let ty = 8;
  ctx.textBaseline = 'top';
  if (t.bar) {
    ctx.fillStyle = t.bar[0]; ctx.fillRect(0, 0, w, 19);
    ctx.fillStyle = t.bar[1]; ctx.font = "700 8.5px 'Space Mono', monospace"; ctx.fillText(label, 8, 5);
    ty = 27;
  }
  ctx.restore();
  if (t.border) { rr(ctx, 0.5, 0.5, w - 1, h - 1, t.rad); ctx.strokeStyle = t.border; ctx.lineWidth = 1; ctx.stroke(); }
  ctx.font = t.font; ctx.fillStyle = t.ink;
  const padL = ch === 'sunny' ? 20 : 9;
  const lines = wrap(ctx, t.upper ? title.toUpperCase() : title, w - padL - 12).slice(0, 3);
  lines.forEach((l, i) => ctx.fillText(l, padL, ty + i * (t.upper ? 13 : 15)));
  // DONE stamp, bottom right, -11deg
  ctx.save();
  ctx.font = "700 9px 'Space Mono', monospace";
  const sw = ctx.measureText('DONE').width + 14, sh = 19;
  ctx.translate(w - 5 - sw / 2, h - 6 - sh / 2); ctx.rotate((-11 * Math.PI) / 180);
  ctx.globalAlpha = 0.9; ctx.strokeStyle = t.stamp; ctx.lineWidth = 2; rr(ctx, -sw / 2, -sh / 2, sw, sh, 7); ctx.stroke();
  ctx.fillStyle = t.stamp; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText('DONE', 0, 1);
  ctx.restore();
  ctx.restore();
}

export async function renderShare(s: ShareSpec): Promise<Blob> {
  const c = C[s.ch];
  try {
    await Promise.all(["italic 27px 'Instrument Serif'", "800 12px 'Figtree'", "700 12px 'Figtree'", "700 13px 'Kalam'", "700 10px 'Space Mono'"].map((f) => document.fonts.load(f)));
  } catch { /* draw with fallbacks */ }
  const cv = document.createElement('canvas');
  cv.width = W * K; cv.height = H * K;
  const ctx = cv.getContext('2d')!;
  ctx.scale(K, K);
  ctx.fillStyle = c.bg; ctx.fillRect(0, 0, W, H);
  try {
    const img = await loadImg(c.assets.key);
    const k = Math.max(W / img.naturalWidth, 372 / img.naturalHeight);
    const dw = img.naturalWidth * k, dh = img.naturalHeight * k;
    ctx.drawImage(img, (W - dw) * 0.5, (372 - dh) * 0.16, dw, dh);
  } catch { /* plain background */ }
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, 'rgba(6,5,4,0.5)'); g.addColorStop(0.15, 'rgba(6,5,4,0)'); g.addColorStop(0.4, 'rgba(6,5,4,0)');
  g.addColorStop(0.66, rgba(c.bg, 0.82)); g.addColorStop(0.8, c.bg); g.addColorStop(1, c.bg);
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);

  // top row: wordmark + streak chip
  ctx.textBaseline = 'top'; ctx.fillStyle = '#F5F1EA'; ctx.font = "800 12px 'Figtree', sans-serif";
  (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = '1.7px';
  ctx.fillText('XIAOMAN', 16, 15);
  (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = '0px';
  if (s.kind !== 'streak') {
    ctx.font = "700 12px 'Figtree', sans-serif";
    const txt = `${s.streak}-day streak`;
    const tw = ctx.measureText(txt).width;
    ctx.fillText(txt, W - 16 - tw, 15);
    flame(ctx, W - 16 - tw - 19, 14, 14, 14, c.acc);
  }

  if (s.kind === 'card') {
    quoteBlock(ctx, s, quoteSize(s.quote), { top: 236, above: H - 128 });
    if (s.ticket) ticket(ctx, s.ch, s.ticket.label, s.ticket.title);
  } else if (s.kind === 'streak') {
    ctx.shadowColor = c.acc; ctx.shadowBlur = 16;
    flame(ctx, 16, 250, 62, 70, c.acc, '#FFE7A8');
    ctx.shadowColor = 'transparent';
    ctx.fillStyle = '#F7EEE3'; ctx.textBaseline = 'alphabetic';
    ctx.font = "800 76px 'Figtree', sans-serif";
    const n = String(s.streak);
    ctx.fillText(n, 88, 318);
    const nw = ctx.measureText(n).width;
    ctx.font = "700 16px 'Figtree', sans-serif";
    ctx.fillText(s.streak === 1 ? 'day' : 'days', 88 + nw + 10, 296); ctx.fillText('in a row', 88 + nw + 10, 314);
    quoteBlock(ctx, s, 24, { bottom: 22 });
  } else {
    ctx.fillStyle = '#F7EEE3'; ctx.textBaseline = 'top'; ctx.font = "800 23px 'Figtree', sans-serif";
    ctx.fillText(s.weekTitle || '', 18, 236);
    const stats = s.weekStats || [];
    const cw = (W - 32 - 6 * (stats.length - 1)) / Math.max(1, stats.length);
    stats.forEach((x, i) => {
      const x0 = 16 + i * (cw + 6);
      rr(ctx, x0, 276, cw, 52, 12); ctx.fillStyle = 'rgba(15,14,12,0.7)'; ctx.fill(); ctx.strokeStyle = 'rgba(255,255,255,0.12)'; ctx.lineWidth = 1; ctx.stroke();
      ctx.fillStyle = c.acc; ctx.font = "800 19px 'Figtree', sans-serif"; ctx.fillText(x.n, x0 + 8, 284);
      ctx.fillStyle = '#E9E4DA'; ctx.font = "600 11.5px 'Figtree', sans-serif"; ctx.fillText(x.t, x0 + 8, 309);
    });
    quoteBlock(ctx, s, 24, { bottom: 22 });
  }
  return await new Promise<Blob>((res, rej) => cv.toBlob((b) => (b ? res(b) : rej(new Error('toBlob'))), 'image/png'));
}
